import { App } from "@capacitor/app"
import { Capacitor, type PluginListenerHandle } from "@capacitor/core"
import type { WorkPeriod } from "@/models/work-period.model"
import { MAX_WORK_PERIOD_DURATION_MS } from "@/helper/models/work-period-rules"
import * as location from "./background-location-service"
import { clearShiftNotification, showShiftNotification } from "./shift-notification-service"
import { isWithinTrackingWindow } from "./work-period-tracking-policy"

type TrackingWorkPeriod = Pick<WorkPeriod, "id" | "timeStart" | "timeEnd">
interface TrackingSession {
  workPeriodId: string
  startsAt: number
  expiresAt: number
}

let session: TrackingSession | null = null
let expiryTimer: ReturnType<typeof setTimeout> | undefined
let appStateListener: PluginListenerHandle | null = null
let operations: Promise<void> = Promise.resolve()

function enqueue(operation: () => Promise<void>) {
  const result = operations.then(operation)
  operations = result.catch(() => {})
  return result
}

function isCurrent(target: TrackingSession): boolean {
  if (session !== target) return false
  if (isWithinTrackingWindow(target.startsAt)) return true
  void recheckTracking()
  return false
}

async function stopSession() {
  session = null
  clearTimeout(expiryTimer)
  expiryTimer = undefined
  void clearShiftNotification()
  try {
    await location.stopLocationTracking()
  } finally {
    const listener = appStateListener
    appStateListener = null
    await listener?.remove()
  }
}

// Every timer/native wake-up checks the same session and original deadline.
function recheckTracking(refreshAuthorization = false): Promise<void> {
  return enqueue(async () => {
    if (!session) return
    if (!isWithinTrackingWindow(session.startsAt)) {
      await stopSession()
      return
    }
    clearTimeout(expiryTimer)
    expiryTimer = setTimeout(() => {
      void recheckTracking()
    }, session.expiresAt - Date.now())
    if (refreshAuthorization) await location.updateAuthorizationToken()
  }).catch((error) => console.warn("Failed to recheck shift tracking:", error))
}

/**
 * Synchronize a persisted shift. Null clears startup state; pass a deleted shift's ID
 * to clear only that shift. Changes to unrelated ineligible shifts leave tracking alone.
 */
export function syncWorkPeriod(
  workPeriod: TrackingWorkPeriod | null,
  deletedWorkPeriodId?: string
): Promise<void> {
  // Snapshot mutable models before waiting for another native operation.
  const workPeriodId = workPeriod?.id
  const startsAt = workPeriod?.timeStart.valueOf()
  const isOpen = workPeriod !== null && !workPeriod.timeEnd
  return enqueue(async () => {
    const eligible =
      isOpen && workPeriodId && startsAt !== undefined && isWithinTrackingWindow(startsAt)
    if (!eligible) {
      const affectedId = workPeriodId ?? deletedWorkPeriodId
      if (
        !affectedId ||
        !session ||
        session.workPeriodId === affectedId ||
        !isWithinTrackingWindow(session.startsAt)
      ) {
        await stopSession()
      }
      return
    }
    if (session?.workPeriodId === workPeriodId && session.startsAt === startsAt) return

    await stopSession()
    const target: TrackingSession = {
      workPeriodId,
      startsAt,
      expiresAt: startsAt + MAX_WORK_PERIOD_DURATION_MS
    }
    session = target
    try {
      await location.trackLocation(startsAt, target.expiresAt, () => isCurrent(target))
      if (!isCurrent(target)) {
        await stopSession()
        return
      }
      expiryTimer = setTimeout(() => {
        void recheckTracking()
      }, target.expiresAt - Date.now())
      if (Capacitor.isNativePlatform()) {
        appStateListener = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void recheckTracking(true)
        })
      }
      // Notifications are optional and never hold up stopping location tracking.
      void showShiftNotification(startsAt, () => isCurrent(target))
    } catch (error) {
      await stopSession()
      throw error
    }
  })
}
